import { MedplumClient, parseReference } from "@medplum/core";
import { Observation, Reference } from "@medplum/fhirtypes";

import { ObservationVersionRow } from "@/encounters/components/EncounterLabor/EncounterLaborVersionDiff/collapse-observation-version-chains";
import { normalizeObservationFormCode } from "@/encounters/components/EncounterLabor/EncounterLaborVersionDiff/structuredMedTherapyDiffConfig";
import {
  getObservationPreviousVersionTargetId,
  resolveLatestObservationVersionLeaf,
  resolveObservationAmendPredecessor,
  resolveObservationAmendPredecessorId,
} from "@/encounters/components/EncounterLabor/utils/observation-version-chain";
import { LOINCCodes } from "@/fhir/enums/loinc";
import { LaborFlowsheetForms } from "@/fhir/enums/quilted-health";
import {
  getQuestionnaireResponseIdFromObservationDerivedFrom,
  joinReferenceString,
} from "@/utils/medplum";
import { pickObservationNewerByVersionIdThenRecencyThenId } from "@/utils/observation-recency";

const LABOR_FLOWSHEET_FORM_CODES = new Set<string>(Object.values(LaborFlowsheetForms));

/** Guards against malformed amend predecessor cycles when walking observation chains. */
const MAX_OBSERVATION_VERSION_CHAIN_DEPTH = 64;

const LABOR_FLOWSHEET_OBSERVATION_DISPLAY_NAMES: Record<string, string> = {
  fetal_heart_rate: "Fetal Heart Rate",
  "55283-6": "Fetal Heart Rate",
  labor_vitals: "Labor Vitals",
  "8716-3": "Labor Vitals",
  contractions: "Contractions",
  cervical_exam: "Cervical Exam",
  "cervical-exam": "Cervical Exam",
  rupture_of_membranes: "Rupture of Membranes",
  rom: "Rupture of Membranes",
  position: "Position",
  assessment_note: "Assessment Note",
  "assessment-note": "Assessment Note",
  birthing: "Birthing",
  inputOutput: "Input/Output",
  "input-output": "Input/Output",
  membranes: "Membranes",
  medsAndTherapies: "Meds & Therapies",
  "meds-therapies": "Meds & Therapies",
  newLocation: "Location",
  location: "Location",
  shoulderDystocia: "Shoulder Dystocia",
  admitDischarge: "Admission & Discharge",
  admissionDischarge: "Admission & Discharge",
  estimatedBloodLoss: "Estimated Blood Loss",
  transport: "Transport",
  resuscitation: "Resuscitation",
};

/** True for labor flowsheet member rows (not labor-stage LOINC parents or postpartum forms). */
export function isLaborFlowsheetMemberObservation(observation: Observation): boolean {
  const code = observation.code?.coding?.[0]?.code;
  if (!code || code === LOINCCodes.LaborStage) {
    return false;
  }
  return LABOR_FLOWSHEET_FORM_CODES.has(code);
}

/**
 * Seeds observation ids from labor stage `hasMember` references (Observation or QuestionnaireResponse).
 */
export function collectLaborStageMemberSeedIdsFromReferences(
  memberReferences: ReadonlyArray<Reference> | undefined,
): { observationIds: Set<string>; questionnaireResponseIds: Set<string> } {
  const observationIds = new Set<string>();
  const questionnaireResponseIds = new Set<string>();

  for (const memberReference of memberReferences ?? []) {
    const [resourceType, referenceId] = parseReference(memberReference);
    if (!referenceId) {
      continue;
    }
    if (resourceType === "Observation") {
      observationIds.add(referenceId);
    } else if (resourceType === "QuestionnaireResponse") {
      questionnaireResponseIds.add(referenceId);
    }
  }

  return { observationIds, questionnaireResponseIds };
}

/**
 * Resolves the current flowsheet Observation for a labor stage `hasMember` reference.
 *
 * Stage rows may reference either `Observation/{id}` or `QuestionnaireResponse/{id}` (flowsheet
 * observations also store the QR on `hasMember`).
 */
export function resolveLaborStageMemberObservationFromReference(
  memberReference: Reference,
  allObservations: ReadonlyArray<Observation>,
): Observation | undefined {
  const [resourceType, referenceId] = parseReference(memberReference);
  if (!referenceId) {
    return undefined;
  }

  if (resourceType === "Observation") {
    return resolveLatestObservationVersionLeaf(referenceId, allObservations);
  }

  if (resourceType === "QuestionnaireResponse") {
    const derivedFromMatches = allObservations.filter(
      (candidate) =>
        getQuestionnaireResponseIdFromObservationDerivedFrom(candidate.derivedFrom) === referenceId,
    );
    if (derivedFromMatches.length === 0) {
      return undefined;
    }

    const activeCandidates = derivedFromMatches.filter(
      (candidate) => candidate.status !== "cancelled",
    );
    const pool = activeCandidates.length > 0 ? activeCandidates : derivedFromMatches;
    return pool.reduce((newest, candidate) =>
      pickObservationNewerByVersionIdThenRecencyThenId(newest, candidate),
    );
  }

  return undefined;
}

/** Display label for labor flowsheet observation codes on version diff. */
export function getLaborFlowsheetObservationDisplayName(code: string | undefined): string {
  const formName = normalizeObservationFormCode(code);

  return (
    LABOR_FLOWSHEET_OBSERVATION_DISPLAY_NAMES[formName ?? ""] ??
    LABOR_FLOWSHEET_OBSERVATION_DISPLAY_NAMES[code ?? ""] ??
    formName ??
    code ??
    "Observation"
  );
}

/**
 * Adds an observation and every amend predecessor reachable from it (cancelled rows included).
 */
export function includeObservationAndAmendPredecessors(
  observation: Observation,
  encounterObservations: ReadonlyArray<Observation>,
  includedObservationIds: Set<string>,
): void {
  const visitedIds = new Set<string>();
  let current: Observation | undefined = observation;

  for (let depth = 0; depth < MAX_OBSERVATION_VERSION_CHAIN_DEPTH && current; depth += 1) {
    if (!current.id || visitedIds.has(current.id)) {
      break;
    }
    visitedIds.add(current.id);
    includedObservationIds.add(current.id);

    const predecessorId = resolveObservationAmendPredecessorId(current, encounterObservations);
    if (!predecessorId) {
      break;
    }
    current = encounterObservations.find((candidate) => candidate.id === predecessorId);
  }
}

/**
 * Flowsheet QuestionnaireResponses charted under a labor stage (`partOf` → stage Observation).
 */
export async function fetchQuestionnaireResponseIdsLinkedToLaborStage(
  medplum: MedplumClient,
  stageObservation: Observation,
  clientId: string,
  encounterId: string,
): Promise<Set<string>> {
  if (!stageObservation.id) {
    return new Set();
  }

  const questionnaireResponses = await medplum.searchResources("QuestionnaireResponse", {
    encounter: joinReferenceString("Encounter", encounterId),
    subject: joinReferenceString("Patient", clientId),
    "part-of": joinReferenceString("Observation", stageObservation.id),
  });

  const linkedIds = new Set<string>();
  for (const questionnaireResponse of questionnaireResponses) {
    if (questionnaireResponse.id) {
      linkedIds.add(questionnaireResponse.id);
    }
  }
  return linkedIds;
}

function resolveNewestActiveObservationForQuestionnaireResponse(
  questionnaireResponseId: string,
  allObservations: ReadonlyArray<Observation>,
): Observation | undefined {
  const candidates = allObservations.filter(
    (candidate) =>
      getQuestionnaireResponseIdFromObservationDerivedFrom(candidate.derivedFrom) ===
        questionnaireResponseId && candidate.status !== "cancelled",
  );
  if (candidates.length === 0) {
    return undefined;
  }
  return candidates.reduce((newest, candidate) =>
    pickObservationNewerByVersionIdThenRecencyThenId(newest, candidate),
  );
}

/**
 * True when a flowsheet member observation belongs on this labor stage.
 * Stale stage `hasMember` references (older Observation ids or QuestionnaireResponse ids) can
 * still resolve to the newest amend version after a row moves stages; QR `partOf` is authoritative.
 */
export function isFlowsheetMemberObservationOnLaborStage(
  memberObservation: Observation,
  stageObservation: Observation,
  linkedQuestionnaireResponseIds: ReadonlySet<string>,
): boolean {
  if (!memberObservation.id) {
    return false;
  }

  const memberObservationReference = joinReferenceString("Observation", memberObservation.id);
  if (
    stageObservation.hasMember?.some((member) => member.reference === memberObservationReference)
  ) {
    return true;
  }

  const questionnaireResponseId = getQuestionnaireResponseIdFromObservationDerivedFrom(
    memberObservation.derivedFrom,
  );
  if (!questionnaireResponseId) {
    return false;
  }

  return linkedQuestionnaireResponseIds.has(questionnaireResponseId);
}

/**
 * Builds version-diff rows for one labor stage from `hasMember` and QR `partOf` links.
 */
export async function collectLaborStageMemberRowsForVersionDiff(
  medplum: MedplumClient,
  stageObservation: Observation,
  laborStageObservations: ReadonlyArray<Observation>,
  clientId: string,
  encounterId: string,
): Promise<ObservationVersionRow[]> {
  const observationsById = new Map(
    laborStageObservations
      .filter(
        (observation): observation is Observation & { id: string } =>
          typeof observation.id === "string" && observation.id.length > 0,
      )
      .map((observation) => [observation.id, observation]),
  );

  const rowsByMemberObservationId = new Map<string, ObservationVersionRow>();

  const addMemberObservationRow = (memberObservation: Observation) => {
    if (!memberObservation.id || memberObservation.status === "cancelled") {
      return;
    }
    rowsByMemberObservationId.set(memberObservation.id, {
      currentObservation: memberObservation,
      previousObservation: resolveObservationAmendPredecessor(
        memberObservation,
        observationsById,
        laborStageObservations,
      ),
    });
  };

  const linkedQuestionnaireResponseIds = await fetchQuestionnaireResponseIdsLinkedToLaborStage(
    medplum,
    stageObservation,
    clientId,
    encounterId,
  );

  for (const memberReference of stageObservation.hasMember ?? []) {
    const memberObservation = resolveLaborStageMemberObservationFromReference(
      memberReference,
      laborStageObservations,
    );
    if (
      memberObservation &&
      isFlowsheetMemberObservationOnLaborStage(
        memberObservation,
        stageObservation,
        linkedQuestionnaireResponseIds,
      )
    ) {
      addMemberObservationRow(memberObservation);
    }
  }

  for (const questionnaireResponseId of linkedQuestionnaireResponseIds) {
    const memberObservation = resolveNewestActiveObservationForQuestionnaireResponse(
      questionnaireResponseId,
      laborStageObservations,
    );
    if (memberObservation) {
      addMemberObservationRow(memberObservation);
    }
  }

  return [...rowsByMemberObservationId.values()];
}

/**
 * Adds labor flowsheet observations linked to seeded stage members (including amend chains).
 */
export function expandIncludedLaborFlowsheetObservationIds(
  encounterObservations: ReadonlyArray<Observation>,
  includedObservationIds: Set<string>,
  includedQuestionnaireResponseIds: Set<string>,
): void {
  for (const observation of encounterObservations) {
    if (!observation.id || includedObservationIds.has(observation.id)) {
      continue;
    }
    const questionnaireResponseId = getQuestionnaireResponseIdFromObservationDerivedFrom(
      observation.derivedFrom,
    );
    if (
      questionnaireResponseId &&
      includedQuestionnaireResponseIds.has(questionnaireResponseId) &&
      isLaborFlowsheetMemberObservation(observation)
    ) {
      includeObservationAndAmendPredecessors(
        observation,
        encounterObservations,
        includedObservationIds,
      );
    }
  }

  for (const observation of encounterObservations) {
    if (!observation.id || includedObservationIds.has(observation.id)) {
      continue;
    }
    if (!isLaborFlowsheetMemberObservation(observation)) {
      continue;
    }
    const predecessorId = resolveObservationAmendPredecessorId(observation, encounterObservations);
    if (predecessorId && includedObservationIds.has(predecessorId)) {
      includeObservationAndAmendPredecessors(
        observation,
        encounterObservations,
        includedObservationIds,
      );
    }
  }
}

/**
 * Expands seeded labor flowsheet ids until amend successors and questionnaire links stabilize.
 */
export function expandIncludedLaborFlowsheetObservationIdsUntilStable(
  encounterObservations: ReadonlyArray<Observation>,
  includedObservationIds: Set<string>,
  includedQuestionnaireResponseIds: Set<string>,
  observationsById: ReadonlyMap<string, Observation>,
): void {
  expandIncludedLaborFlowsheetObservationIds(
    encounterObservations,
    includedObservationIds,
    includedQuestionnaireResponseIds,
  );

  let expanded = true;
  while (expanded) {
    expanded = false;

    for (const observation of encounterObservations) {
      if (!observation.id || includedObservationIds.has(observation.id)) {
        continue;
      }
      const previousVersionTargetId = getObservationPreviousVersionTargetId(observation);
      if (previousVersionTargetId && includedObservationIds.has(previousVersionTargetId)) {
        includeObservationAndAmendPredecessors(
          observation,
          encounterObservations,
          includedObservationIds,
        );
        expanded = true;
      }
    }

    for (const observationId of includedObservationIds) {
      const observation = observationsById.get(observationId);
      const questionnaireResponseId = observation
        ? getQuestionnaireResponseIdFromObservationDerivedFrom(observation.derivedFrom)
        : undefined;
      if (questionnaireResponseId) {
        includedQuestionnaireResponseIds.add(questionnaireResponseId);
      }
    }

    const sizeBeforeQuestionnaireExpansion = includedObservationIds.size;
    expandIncludedLaborFlowsheetObservationIds(
      encounterObservations,
      includedObservationIds,
      includedQuestionnaireResponseIds,
    );
    if (includedObservationIds.size > sizeBeforeQuestionnaireExpansion) {
      expanded = true;
    }
  }
}
